/* eslint-disable @typescript-eslint/no-explicit-any */
import { NextResponse } from "next/server";

// Base response interface
export interface ApiResponseData<T = any> {
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
  meta?: any;
}

// Pagination metadata
export interface PaginationMeta {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Paginated response interface
export interface PaginatedApiResponse<T = any>
  extends Omit<ApiResponseData<T>, "data" | "success"> {
  data: T[];
  pagination: PaginationMeta;
}

// Error response interface
export interface ErrorResponse extends ApiResponseData {
  error?: {
    code: string;
    details?: any;
  };
}

// HTTP status codes enum
enum HttpStatus {
  OK = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  UNPROCESSABLE_ENTITY = 422,
  INTERNAL_SERVER_ERROR = 500,
}

// Error types enum
export enum ErrorType {
  NO_SUBSCRIPTION_FOUND = "NO_SUBSCRIPTION_FOUND",
  NO_CREDITS_FOUND = "NO_CREDITS_FOUND",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
  AUTHORIZATION_ERROR = "AUTHORIZATION_ERROR",
  NOT_FOUND_ERROR = "NOT_FOUND_ERROR",
  BAD_REQUEST_ERROR = "BAD_REQUEST_ERROR",
}

/**
 * Standardized API Response class
 */
export class ApiResponse {
  static ok(): NextResponse {
    return NextResponse.json("Ok", { status: HttpStatus.OK });
  }
  /**
   * Create a successful response
   */
  static success<T extends object = any>(
    options: {
      data?: T;
      meta?: Partial<ApiResponseData["meta"]>;
      status?: number;
      extra?: Record<string, any>;
    } = {},
    flatten = false,
  ): NextResponse {
    const { data, meta, status, extra } = options;
    const response: ApiResponseData<T> = flatten
      ? {
        ...extra,
        ...data,
        ...meta,
      }
      : {
        ...extra,
        data,
        meta,
      };

    return NextResponse.json(response, { status });
  }

  /**
   * Create a successful response with pagination
   */
  static paginated<T>(
    data: T[],
    pagination: PaginationMeta,
    status: number = HttpStatus.OK,
    meta?: Partial<ApiResponseData["meta"]>,
  ): NextResponse {
    const response: PaginatedApiResponse<T> = {
      data,
      pagination,
      meta: {
        timestamp: new Date().toISOString(),
        ...meta,
      },
    };

    return NextResponse.json(response, { status });
  }

  /**
   * Create an error response
   */
  static error(
    message: string,
    status: number = HttpStatus.BAD_REQUEST,
    errorType: ErrorType = ErrorType.BAD_REQUEST_ERROR,
    errors?: Record<string, string[]>,
    details?: any,
  ): NextResponse {
    const response: ErrorResponse = {
      message,
      errors,
      error: {
        code: errorType,
        details,
      },
    };

    return NextResponse.json(response, { status });
  }

  /**
   * Create a validation error response
   */
  static validationError(
    errors: Record<string, string[]>,
    message: string = "Validation failed",
  ): NextResponse {
    return ApiResponse.error(
      message,
      HttpStatus.UNPROCESSABLE_ENTITY,
      ErrorType.VALIDATION_ERROR,
      errors,
    );
  }

  /**
   * Create an authentication error response
   */
  static unauthorized(message: string = "Unauthorized"): NextResponse {
    return ApiResponse.error(
      message,
      HttpStatus.UNAUTHORIZED,
      ErrorType.AUTHENTICATION_ERROR,
    );
  }

  /**
   * Create an authorization error response
   */
  static forbidden(message: string = "Forbidden"): NextResponse {
    return ApiResponse.error(
      message,
      HttpStatus.FORBIDDEN,
      ErrorType.AUTHORIZATION_ERROR,
    );
  }

  /**
   * Create a not found error response
   */
  static notFound(message: string = "Resource not found"): NextResponse {
    return ApiResponse.error(
      message,
      HttpStatus.NOT_FOUND,
      ErrorType.NOT_FOUND_ERROR,
    );
  }

  /**
   * Create a bad request error response
   */
  static badRequest(message: string = "Bad request"): NextResponse {
    return ApiResponse.error(
      message,
      HttpStatus.BAD_REQUEST,
      ErrorType.BAD_REQUEST_ERROR,
    );
  }

  /**
   * Create a created response (for POST requests)
   */
  static created<T extends object>(data: T): NextResponse {
    return ApiResponse.success({
      data,
      status: HttpStatus.CREATED,
    });
  }

  /**
   * Create a no content response (for DELETE requests)
   */
  static noContent(): NextResponse {
    return new NextResponse(null, { status: HttpStatus.NO_CONTENT });
  }

  /**
   * Create a custom response with specific data structure
   */
  static custom<T>(
    data: T,
    status: number = HttpStatus.OK,
    headers?: Record<string, string>,
  ): NextResponse {
    return NextResponse.json(data, { status, headers });
  }

  private static stream(
    stream: ReadableStream,
    status: number = HttpStatus.OK,
    headers?: Record<string, string>,
  ): NextResponse {
    return new NextResponse(stream, {
      status,
      headers: {
        "Content-Type": "text/event-stream",
        "Cache-Control": "no-cache, no-transform",
        Connection: "keep-alive",
        "X-Accel-Buffering": "no",
        ...headers,
      },
    });
  }

  static streamOk(
    stream: ReadableStream,
    headers?: Record<string, string>,
  ): NextResponse {
    return ApiResponse.stream(stream, HttpStatus.OK, headers);
  }
}
